import '@fontsource/opendyslexic';
import { ensureConnection } from 'src/features/companyProjects/realtime/dashboard/projectDashboardHub';
import { Box } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import dayjsPluginUTC from 'dayjs/plugin/utc';
import { useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import ActivityOpenNotificationProvider from '../features/activities/editActivity/activityOpenNotification/ActivityOpenNotificationProvider.tsx';
import { useFrontierAuth } from '../util/auth/useFrontierAuth.ts';
import { ErrorContainer } from './ErrorContainer.tsx';
import { useNavWithRouting } from './routes/useNavWithRouting.tsx';
import { ChakraRootProvider } from '../theme/provider.tsx';
import { AppToaster, ChakraBreakpoints, useBetaStore } from '@cks-internal/react-base';
import { ApplicationRoles } from '../constants/applicationRoles.ts';
import { AgentDrawerHub } from '../features/agentDrawer/hubs/AgentDrawerHub.tsx';
import SystemMessageNotification from '../components/Common/SystemMessageNotification.tsx';
import { ActivityInformationContextProvider } from '../features/activities/informationDisplays/context/activityInformationContext.tsx';
import { ActivityInformationFooter } from '../features/activities/informationDisplays/context/activityInformationFooterPopouts.tsx';
import { usePerformanceFlagStore } from '../stores/performanceFlagStore.ts';
import { ConditionalProvider } from '../common/ConditionalProvider.tsx';
import { InboxHub } from '../features/agentInbox/hubs/InboxHub.tsx';
import { EnhancedBatchUpdateWorker } from '../features/agentInbox/utils/worker/UpdateBatcher.tsx';
import { FolioRequestsHub } from '../features/folioRequests/hubs/FolioRequestsHub.tsx';
import { ProjectIntakeInboxHub } from '../features/companyProjects/projectIntake/inbox/hubs/ProjectIntakeInboxHub.tsx';
import { ProjectDashboardRealtimeBridge } from '../features/companyProjects/realtime/dashboard/ProjectDashboardRealtimeBridge.tsx';
import { pdfjs } from 'react-pdf';
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { API_URL } from '../lib/config.ts';
import { ApolloProvider } from '@apollo/client/react';
import { configGlobalAxios } from 'src/lib/axios.tsx';

window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

//This adds support for UTC and local timezone conversions to dayjs
dayjs.extend(dayjsPluginUTC);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(isSameOrBeforePlugin);

const tanStackClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchOnMount: true,
      staleTime: 0,
      gcTime: 0
    }
  }
});

useBetaStore.getState().setBeta(false);
export let portalRef: any;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
configGlobalAxios();

const client = new ApolloClient({
  link: new HttpLink({
    uri: `${API_URL}/graphql`,
    fetchOptions: {
      credentials: 'include'
    }
  }),
  cache: new InMemoryCache()
});

const App = () => {
  const { initUser, hasLoaded, isAuthenticated, isInRole } = useFrontierAuth();
  const portalRef = useRef(null);
  const performanceFlagStore = usePerformanceFlagStore();

  window.global ||= window;

  const AppRouter = createBrowserRouter(useNavWithRouting());

  // Start projectDashboard hub only after staff auth so negotiate carries cookies (avoids churn / missed inbox events).
  useEffect(() => {
    if (!hasLoaded || !isAuthenticated || !isInRole([ApplicationRoles.staff])) {
      return;
    }
    void ensureConnection();
  }, [hasLoaded, isAuthenticated, isInRole]);

  useEffect(() => {
    async function initAuth() {
      await initUser();
    }

    initAuth().then();
  }, []);

  if (!hasLoaded || !isAuthenticated || !isInRole([ApplicationRoles.staff])) {
    return (
      <ChakraRootProvider>
        <QueryClientProvider client={tanStackClient}>
          <AppToaster />
          {!performanceFlagStore.disableSystemMessageNotification && <SystemMessageNotification />}
          <ErrorContainer>
            <Box ref={portalRef}>{hasLoaded && <RouterProvider router={AppRouter} />}</Box>
          </ErrorContainer>
        </QueryClientProvider>
      </ChakraRootProvider>
    );
  }

  return (
    <ApolloProvider client={client}>
      <ChakraRootProvider>
        <InboxHub />
        <AgentDrawerHub />
        <EnhancedBatchUpdateWorker />
        <QueryClientProvider client={tanStackClient}>
          <FolioRequestsHub />
          <ProjectDashboardRealtimeBridge />
          <ProjectIntakeInboxHub />
          <AppToaster />
          {!performanceFlagStore.disableSystemMessageNotification && <SystemMessageNotification />}
          <ConditionalProvider
            condition={!performanceFlagStore.disableActivityOpenNotification}
            provider={ActivityOpenNotificationProvider}>
            <ActivityInformationContextProvider>
              <ChakraBreakpoints />
              <ErrorContainer>
                <Box ref={portalRef}>
                  {hasLoaded && <RouterProvider router={AppRouter} />}
                  {!performanceFlagStore.disableFooterTabs && <ActivityInformationFooter />}
                </Box>
              </ErrorContainer>
            </ActivityInformationContextProvider>
          </ConditionalProvider>
        </QueryClientProvider>
      </ChakraRootProvider>
    </ApolloProvider>
  );
};

export default App;
